import { useState, useRef, useCallback, useEffect } from "react";

// Extend window for webkitSpeechRecognition
declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

interface UseSpeechOptions {
  lang?: string;
  rate?: number;
  pitch?: number;
  onVoiceResult?: (transcript: string) => void;
}

export function useSpeech(options: UseSpeechOptions = {}) {
  const { lang = "ar-SA", rate = 0.85, pitch = 1.1, onVoiceResult } = options;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceResult, setVoiceResult] = useState<string | null>(null);
  const [voicesReady, setVoicesReady] = useState(false);
  const synthRef = useRef(window.speechSynthesis);
  const recognitionRef = useRef<any>(null);
  const onVoiceResultRef = useRef(onVoiceResult);
  onVoiceResultRef.current = onVoiceResult;

  // Preload voices
  useEffect(() => {
    const loadVoices = () => {
      const voices = synthRef.current.getVoices();
      if (voices.length > 0) setVoicesReady(true);
    };
    loadVoices();
    synthRef.current.addEventListener("voiceschanged", loadVoices);
    return () => {
      synthRef.current.removeEventListener("voiceschanged", loadVoices);
      synthRef.current.cancel();
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
      }
    };
  }, []);

  const speak = useCallback((text: string) => {
    const synth = synthRef.current;
    synth.cancel();
    
    const doSpeak = () => {
      const voices = synth.getVoices();
      const isArabic = lang.startsWith("ar");
      const targetLang = isArabic ? "ar" : lang.split("-")[0];

      const voice = voices.find((v: SpeechSynthesisVoice) =>
        v.lang.startsWith(targetLang) && v.name.toLowerCase().includes("google")
      ) || voices.find((v: SpeechSynthesisVoice) => v.lang.startsWith(targetLang)) || null;

      const utt = new SpeechSynthesisUtterance(text);
      if (voice) {
        utt.voice = voice;
        utt.lang = voice.lang;
      } else {
        utt.lang = lang;
      }
      utt.rate = rate;
      utt.pitch = pitch;
      utt.onend = () => setIsSpeaking(false);
      utt.onerror = () => setIsSpeaking(false);
      setIsSpeaking(true);
      synth.speak(utt);
    };

    // If voices not loaded yet, wait for them
    if (synth.getVoices().length === 0) {
      const handler = () => {
        synth.removeEventListener("voiceschanged", handler);
        doSpeak();
      };
      synth.addEventListener("voiceschanged", handler);
      // Fallback timeout
      setTimeout(() => {
        synth.removeEventListener("voiceschanged", handler);
        doSpeak();
      }, 500);
    } else {
      doSpeak();
    }
  }, [lang, rate, pitch]);

  const stopSpeaking = useCallback(() => {
    synthRef.current.cancel();
    setIsSpeaking(false);
  }, []);

  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceResult("⚠️ متصفحك لا يدعم التعرف على الكلام. يرجى استخدام Chrome.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setVoiceResult(transcript);
      setIsListening(false);
      onVoiceResultRef.current?.(transcript);
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
    setVoiceResult(null);
  }, [lang]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }
    setIsListening(false);
  }, []);

  const clearVoiceResult = useCallback(() => setVoiceResult(null), []);

  const ENCOURAGEMENTS_AR = [
    "أحسنت يا بطل!",
    "ممتاز!",
    "رائع جداً!",
    "يا سلام عليك!",
    "برافو!",
    "عبقري!",
    "شاطر!",
    "هيا استمر!",
    "ما شاء الله!",
    "بطل بطل!",
  ];

  const ENCOURAGEMENTS_EN = [
    "Great job!",
    "Awesome!",
    "Well done!",
    "Fantastic!",
    "You're a star!",
    "Amazing!",
  ];

  const WRONG_AR = [
    "حاول مرة أخرى!",
    "لا بأس، حاول ثانية!",
    "قريب! جرب مرة أخرى",
    "لا تستسلم!",
    "فكّر شوي وحاول!",
    "يلا نحاول مرة ثانية!",
    "ركّز وحاول!",
  ];

  const WRONG_EN = [
    "Try again!",
    "Almost! Try once more",
    "Don't give up!",
    "So close! Try again",
    "Keep trying!",
  ];

  const encourageCorrect = useCallback(() => {
    const isEnglish = lang.startsWith("en");
    const phrases = isEnglish ? ENCOURAGEMENTS_EN : ENCOURAGEMENTS_AR;
    const phrase = phrases[Math.floor(Math.random() * phrases.length)];
    speak(phrase);
  }, [lang, speak]);

  const encourageWrong = useCallback(() => {
    const isEnglish = lang.startsWith("en");
    const phrases = isEnglish ? WRONG_EN : WRONG_AR;
    const phrase = phrases[Math.floor(Math.random() * phrases.length)];
    speak(phrase);
  }, [lang, speak]);

  return {
    isSpeaking, isListening, voiceResult,
    speak, stopSpeaking, encourageCorrect, encourageWrong,
    startListening, stopListening, clearVoiceResult,
  };
}
